import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchCardDataAsync,
  fetchCardDatesAsync,
  fetchCardDataStreamAsync,
  StreamingCardDataResponse,
  fetchCardTypesAsync,
} from './RodLiftAnalysisService';
import { getCardColor } from '../../utilities/CardColorGenerator';
import { ALLY_CONFIGURATIONS } from '../../services/configuration/config';
import { showWarningAlert } from '../../components/toast/toastBridge';
import { generateNormalizedCacheKey } from '../../utilities/DateNormalizationUtil';

interface RGBColor {
  r: number;
  g: number;
  b: number;
}

interface CardTypeItem {
  cardTypeCode: string;
  cardTypeDescription: string;
}

interface CardDateItemState {
  date: string;
  cardTypeId: string;
  cardTypeName: string;
  isSelected: boolean;
  id: number;
  color: RGBColor;
  downHoleStroke?: number;
  pumpEfficiency?: number | null;
  pumpFillage?: number | null;
  pumpFillageSetpoint?: number | null;
  secondaryFillageSetpoint?: number | null;
  inferredProduction?: number | null;
  runtime?: number | null;
  strokesPerMinute?: number | null;
  minPolishedRodLoad?: number | null;
  maxPolishedRodLoad?: number | null;
  fluidPound?: number | null;
  buoyantRodWeight?: number | null;
  dryRodWeight?: number | null;
  pumpFriction?: number | null;
  poFluidLoad?: number | null;
  upliftOpportunity?: boolean;
  rodsOverloaded?: boolean | null;
  pumpTagging?: boolean;
  gearboxOverloaded?: boolean;
  svLeak?: number | null;
  standingValveLeak?: boolean;
}

interface CardDateListState {
  values: CardDateItemState[];
  selectedCardDates: string[];
  colorIds: number[];
  assetId: string;
  isLoading: boolean;
  isStreaming: boolean;
  streamingComplete: boolean;
  chunksReceived: number;
  cardTypes: CardTypeItem[];
  isLoadingCardTypes: boolean;
  highlightRow: string;
}

interface FetchCardDataParams {
  assetId: string;
  version: 'v1' | 'v2' | 'v3';
  startDate?: string;
  endDate?: string;
}

const fetchRodLiftAnalysisCardDatesAsync = createAsyncThunk(
  'RodLiftAnalysis/fetchCardDates',
  async ({ assetId, version, startDate, endDate }: FetchCardDataParams) => {
    if (version === 'v2') {
      const response = await fetchCardDataAsync(assetId, startDate ?? '', endDate ?? '');
      return {
        values: response.cardDataItems.map((item: any) => ({
          date: item.cardDate,
          cardTypeId: item.cardTypeId,
          cardTypeName: item.cardTypeDescription,
          id: Math.random(),
          isSelected: false,
          downHoleStroke: item.downHoleStroke,
          pumpEfficiency: item.pumpEfficiency,
          pumpFillage: item.pumpFillage,
          pumpFillageSetpoint: item.pumpFillageSetpoint,
          secondaryFillageSetpoint: item.secondaryFillageSetpoint,
          inferredProduction: item.inferredProduction,
          runtime: item.runtime,
          strokesPerMinute: item.strokesPerMinute,
          minPolishedRodLoad: item.minPolishedRodLoad,
          maxPolishedRodLoad: item.maxPolishedRodLoad,
          fluidPound: item.fluidPound,
          buoyantRodWeight: item.buoyantRodWeight,
          dryRodWeight: item.dryRodWeight,
          pumpFriction: item.pumpFriction,
          poFluidLoad: item.poFluidLoad,
          upliftOpportunity: item.upliftOpportunity,
          rodsOverloaded: item.rodsOverloaded,
          pumpTagging: item.pumpTagging,
          gearboxOverloaded: item.gearboxOverloaded,
          svLeak: item.svLeak,
          standingValveLeak: item.standingValveLeak,
        })),
      };
    } else {
      const response = await fetchCardDatesAsync(assetId);
      return response;
    }
  },
);

// Async thunk for fetching card types
const fetchRodLiftAnalysisCardTypesAsync = createAsyncThunk(
  'RodLiftAnalysis/fetchCardTypes',
  async (assetId: string) => {
    const response = await fetchCardTypesAsync(assetId);
    return response.values || response;
  },
);

// Track active thunk requests to prevent duplicates
const activeThunkRequests = new Set<string>();

// Async thunk for v3 streaming data
const fetchRodLiftAnalysisCardDatesStreamAsync = createAsyncThunk(
  'RodLiftAnalysis/fetchCardDatesStream',
  async ({ assetId, startDate, endDate }: FetchCardDataParams, { dispatch }) => {
    const requestKey = generateNormalizedCacheKey(assetId, startDate, endDate);

    const promise = new Promise<{ assetId: string }>((resolve, reject) => {
      let hasReceivedFirstChunk = false;

      const onChunkReceived = (chunk: StreamingCardDataResponse) => {
        dispatch(addStreamingChunk(chunk));

        if (!hasReceivedFirstChunk) {
          hasReceivedFirstChunk = true;
          // Resolve immediately after first chunk to indicate streaming has started
          resolve({ assetId });
        }

        if (chunk.isComplete) {
          dispatch(completeStreaming());
          // Clean up when streaming is complete
          activeThunkRequests.delete(requestKey);
        }
      };

      fetchCardDataStreamAsync(assetId, startDate ?? '', endDate ?? '', onChunkReceived).catch((error) => {
        activeThunkRequests.delete(requestKey);
        if (!hasReceivedFirstChunk) {
          reject(error);
        } else {
          console.error('Error in streaming after first chunk:', error);
        }
      });
    });

    // Also clean up if the promise is rejected at the thunk level
    promise.catch(() => {
      activeThunkRequests.delete(requestKey);
    });

    return promise;
  },
  {
    condition: ({ assetId, startDate, endDate }) => {
      const requestKey = generateNormalizedCacheKey(assetId, startDate, endDate);

      // Check if there's already an active thunk request
      if (activeThunkRequests.has(requestKey)) {
        console.log(`Condition --Request already in progress for key: ${requestKey}`);
        return false;
      }

      // Mark as active in the condition check to prevent race conditions
      activeThunkRequests.add(requestKey);
      console.log(`Condition --Allowing request for key: ${requestKey}`);
      return true;
    },
  },
);

const initialState: CardDateListState = {
  values: [],
  selectedCardDates: [],
  colorIds: [],
  assetId: '',
  isLoading: false,
  isStreaming: false,
  streamingComplete: false,
  chunksReceived: 0,
  cardTypes: [],
  isLoadingCardTypes: false,
  highlightRow: '',
};

const rodLiftAnalysisCardDateSlice = createSlice({
  name: 'RodLiftAnalysis/CardDate',
  initialState,
  reducers: {
    toggleSelected(state, action: PayloadAction<string>) {
      const date = action.payload;

      if (
        !state.selectedCardDates.includes(date) &&
        state.selectedCardDates.length >= ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE
      ) {
        showWarningAlert(`You can add only ${ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE} cards to the chart.`);
        return;
      }
      state.highlightRow = '';
      state.values.forEach((x) => {
        if (x.date == date) {
          if (x.isSelected) {
            const indexToRemove = state.selectedCardDates.findIndex((date) => date == x.date);
            state.selectedCardDates.splice(indexToRemove, 1);
            state.colorIds.splice(indexToRemove, 1);
          } else {
            const colorIndex = getNextColorId(state.colorIds);
            x.color = getCardColor(colorIndex);
            state.colorIds.push(colorIndex);
            state.selectedCardDates.push(x.date);
          }

          x.isSelected = !x.isSelected;
        }
      });
    },
    toggleAllSelected(state, action: PayloadAction<string[]>) {
      let limitExceeded = false;
      action.payload.forEach((date) => {
        if (limitExceeded) {
          return;
        }
        if (
          !state.selectedCardDates.includes(date) &&
          state.selectedCardDates.length >= ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE
        ) {
          limitExceeded = true;
          return;
        }
        state.highlightRow = '';
        state.values.forEach((x) => {
          if (x.date == date) {
            if (x.isSelected) {
              const indexToRemove = state.selectedCardDates.findIndex((date) => date == x.date);
              state.selectedCardDates.splice(indexToRemove, 1);
              state.colorIds.splice(indexToRemove, 1);
            } else {
              const colorIndex = getNextColorId(state.colorIds);
              x.color = getCardColor(colorIndex);
              state.colorIds.push(colorIndex);
              state.selectedCardDates.push(x.date);
            }

            x.isSelected = !x.isSelected;
          }
        });
      });
    },
    updateSelectedCards(state, action: PayloadAction<string[]>) {
      const dates = action.payload;
      const newDates = dates.filter((date) => !state.selectedCardDates.includes(date));
      if (state.selectedCardDates.length + newDates.length > ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE) {
        showWarningAlert(`You can add only ${ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE} cards to the chart.`);
        return;
      }
      state.values.forEach((x) => {
        if (dates.includes(x.date)) {
          if (!x.isSelected) {
            const colorIndex = getNextColorId(state.colorIds);
            x.color = getCardColor(colorIndex);
            x.isSelected = true;
            state.colorIds.push(colorIndex);
            state.selectedCardDates.push(x.date);
          }
        }
      });
    },
    removeSelectedCards(state, action: PayloadAction<string[]>) {
      const dates = action.payload;
      state.highlightRow = '';
      state.values.forEach((x) => {
        if (dates.includes(x.date)) {
          if (x.isSelected) {
            const indexToRemove = state.selectedCardDates.findIndex((date) => date == x.date);
            state.selectedCardDates.splice(indexToRemove, 1);
            state.colorIds.splice(indexToRemove, 1);
            x.isSelected = false;
          }
        }
      });
    },
    resetCardDateListState(state) {
      // Clear active requests when resetting state
      activeThunkRequests.clear();
      // Reset state but preserve assetId to avoid issues
      const currentAssetId = state.assetId;
      return { ...initialState, assetId: currentAssetId };
    },
    clearSelectedCards(state) {
      // Unselect all cards except the first one
      state.values.forEach((item, index) => {
        item.isSelected = index === 0; // Keep first card selected, unselect others
      });

      // Clear arrays and reset with first card if it exists
      state.selectedCardDates = [];
      state.colorIds = [];

      // If there are cards, keep the first one selected
      if (state.values.length > 0) {
        const firstCard = state.values[0];
        const colorIndex = getNextColorId(state.colorIds);
        firstCard.color = getCardColor(colorIndex);
        firstCard.isSelected = true;
        state.selectedCardDates.push(firstCard.date);
        state.colorIds.push(colorIndex);
      }
      state.highlightRow = '';
    },
    addStreamingChunk(state, action: PayloadAction<StreamingCardDataResponse>) {
      const chunk = action.payload;

      // Convert chunk data to CardDateItemState format
      const newItems = chunk.cardDataItems.map((item: any) => ({
        date: item.cardDate,
        cardTypeId: item.cardTypeId,
        cardTypeName: item.cardTypeDescription,
        id: Math.random(),
        isSelected: false,
        downHoleStroke: item.downHoleStroke,
        pumpEfficiency: item.pumpEfficiency,
        pumpFillage: item.pumpFillage,
        pumpFillageSetpoint: item.pumpFillageSetpoint,
        secondaryFillageSetpoint: item.secondaryFillageSetpoint,
        inferredProduction: item.inferredProduction,
        runtime: item.runtime,
        strokesPerMinute: item.strokesPerMinute,
        minPolishedRodLoad: item.minPolishedRodLoad,
        maxPolishedRodLoad: item.maxPolishedRodLoad,
        fluidPound: item.fluidPound,
        buoyantRodWeight: item.buoyantRodWeight,
        dryRodWeight: item.dryRodWeight,
        pumpFriction: item.pumpFriction,
        poFluidLoad: item.poFluidLoad,
        upliftOpportunity: item.upliftOpportunity,
        rodsOverloaded: item.rodsOverloaded,
        pumpTagging: item.pumpTagging,
        gearboxOverloaded: item.gearboxOverloaded,
        svLeak: item.svLeak,
        standingValveLeak: item.standingValveLeak,
      })) as CardDateItemState[];

      // Add new items to the existing values
      state.values.push(...newItems);
      state.chunksReceived++;

      if (chunk.isComplete) {
        state.streamingComplete = true;
        state.isStreaming = false;
        state.isLoading = false;
      }
    },
    completeStreaming(state) {
      state.streamingComplete = true;
      state.isStreaming = false;
      state.isLoading = false;
    },
    addSingleCardItem(state, action: PayloadAction<{ cardItem: CardDateItemState; shouldSelect?: boolean }>) {
      const { cardItem: newItem, shouldSelect = true } = action.payload;

      if (shouldSelect) {
        // First, unselect all previously selected cards
        state.values.forEach((item) => {
          item.isSelected = false;
        });

        // Clear the selected dates and color IDs arrays
        state.selectedCardDates = [];
        state.colorIds = [];
      }

      // Check if card with this date already exists
      const existingIndex = state.values.findIndex((item) => item.date === newItem.date);

      if (existingIndex >= 0) {
        // Update existing item
        if (shouldSelect) {
          const colorIndex = getNextColorId(state.colorIds);
          newItem.color = getCardColor(colorIndex);
          newItem.isSelected = true;

          // Add to selected dates and color IDs
          state.selectedCardDates.push(newItem.date);
          state.colorIds.push(colorIndex);
        } else {
          newItem.isSelected = false;
        }

        state.values[existingIndex] = newItem;
      } else {
        // Add new item
        if (shouldSelect) {
          const colorIndex = getNextColorId(state.colorIds);
          newItem.color = getCardColor(colorIndex);
          newItem.isSelected = true;

          // Add to selected dates and color IDs
          state.selectedCardDates.push(newItem.date);
          state.colorIds.push(colorIndex);
        } else {
          newItem.isSelected = false;
        }

        // Add new item at the beginning (first row) of the table
        state.values.unshift(newItem);
      }
    },
    highlightRow(state, action: PayloadAction<string>) {
      state.highlightRow = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRodLiftAnalysisCardDatesAsync.pending, () => {
      return {
        ...initialState,
        isLoading: true,
      };
    });
    builder.addCase(fetchRodLiftAnalysisCardDatesAsync.fulfilled, (_, action: any) => {
      const assetId = action.meta.arg;

      if (action?.payload?.values === undefined) {
        return { ...initialState, isLoading: false };
      }

      const values = Array.isArray(action.payload?.values)
        ? action.payload?.values?.map(
            (x: any) =>
              ({
                date: x.date,
                cardTypeId: x.cardTypeId,
                cardTypeName: x.cardTypeName,
                id: x.id,
                isSelected: false,
                // Optional fields from v2
                downHoleStroke: x.downHoleStroke,
                pumpEfficiency: x.pumpEfficiency,
                pumpFillage: x.pumpFillage,
                pumpFillageSetpoint: x.pumpFillageSetpoint,
                secondaryFillageSetpoint: x.secondaryFillageSetpoint,
                inferredProduction: x.inferredProduction,
                runtime: x.runtime,
                strokesPerMinute: x.strokesPerMinute,
                minPolishedRodLoad: x.minPolishedRodLoad,
                maxPolishedRodLoad: x.maxPolishedRodLoad,
                fluidPound: x.fluidPound,
                buoyantRodWeight: x.buoyantRodWeight,
                dryRodWeight: x.dryRodWeight,
                pumpFriction: x.pumpFriction,
                poFluidLoad: x.poFluidLoad,
                upliftOpportunity: x.upliftOpportunity,
                rodsOverloaded: x.rodsOverloaded,
                pumpTagging: x.pumpTagging,
                gearboxOverloaded: x.gearboxOverloaded,
                svLeak: x.svLeak,
                standingValveLeak: x.standingValveLeak,
              }) as CardDateItemState,
          )
        : [];

      return {
        ...initialState,
        assetId: assetId,
        values: values,
        isLoading: false,
      };
    });
    builder.addCase(fetchRodLiftAnalysisCardDatesAsync.rejected, (state) => {
      state.isLoading = false;
    });

    // Streaming async thunk handlers
    builder.addCase(fetchRodLiftAnalysisCardDatesStreamAsync.pending, (_, action: any) => {
      const { assetId } = action.meta.arg;
      return {
        ...initialState,
        assetId,
        isLoading: true,
        isStreaming: true,
        streamingComplete: false,
        chunksReceived: 0,
      };
    });
    builder.addCase(fetchRodLiftAnalysisCardDatesStreamAsync.fulfilled, (state) => {
      // First chunk received - continue streaming
      state.isLoading = false;
      state.isStreaming = true;
    });
    builder.addCase(fetchRodLiftAnalysisCardDatesStreamAsync.rejected, (state) => {
      state.isLoading = false;
      state.isStreaming = false;
      state.streamingComplete = false;
    });

    // Card types async thunk handlers
    builder.addCase(fetchRodLiftAnalysisCardTypesAsync.pending, (state) => {
      state.isLoadingCardTypes = true;
    });
    builder.addCase(fetchRodLiftAnalysisCardTypesAsync.fulfilled, (state, action) => {
      state.cardTypes = action.payload;
      state.isLoadingCardTypes = false;
    });
    builder.addCase(fetchRodLiftAnalysisCardTypesAsync.rejected, (state) => {
      state.isLoadingCardTypes = false;
      state.cardTypes = [];
    });
  },
});

// Helper Methods
const getNextColorId = (colorIds: number[]) => {
  if (colorIds.length == 0) {
    return 1;
  }

  const expectedValues = Array.from({ length: colorIds.length + 1 }, (_, i) => i + 1);
  const nextValue = expectedValues.find((x) => !colorIds.includes(x)) || expectedValues.length + 1;

  return nextValue;
};

export {
  CardDateItemState,
  CardTypeItem,
  fetchRodLiftAnalysisCardDatesAsync,
  fetchRodLiftAnalysisCardDatesStreamAsync,
  fetchRodLiftAnalysisCardTypesAsync,
};
export const {
  toggleSelected,
  updateSelectedCards,
  clearSelectedCards,
  resetCardDateListState,
  addStreamingChunk,
  completeStreaming,
  toggleAllSelected,
  addSingleCardItem,
  highlightRow,
  removeSelectedCards,
} = rodLiftAnalysisCardDateSlice.actions;
export default rodLiftAnalysisCardDateSlice.reducer;
